import React, { useState, useEffect, useCallback } from 'react';
import { useHistory } from 'react-router-dom';
import { Trans } from '@lingui/macro';
import { useNotification } from '@landr/maestro';
import { EventIds } from 'types/enums';
import { log } from 'utils/log';
import authenticationService from 'utils/authentication';
import { CONFIG } from '../../utils/config';
import { AuthenticationContextType } from './AuthenticationContext';

type validateTokenPromise = {
    status: number;
    labels: string[];
    artists: string[];
    providerName: string;
    canUploadMetadata: boolean;
};

const validateToken = async (
    accessToken?: string,
): Promise<validateTokenPromise> => {
    const url = `${CONFIG.INGESTION_API_URL}/v1/providers/me`;
    const headers = {
        Authorization: `Bearer ${accessToken}`,
    };

    const response = await fetch(url, { headers: headers }).then((response) => {
        if (response.status >= 400 && response.status < 600) {
            const errorMsg = `Server return a ${response.status}: ${response.statusText}`;

            throw new Error(errorMsg);
        }

        return response;
    });

    return await response.json();
};

export const useAuthentication = (): AuthenticationContextType => {
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const [authToken, setAuthToken] = useState('');
    const [labels, setLabels] = useState<string[]>([]);
    const [artists, setArtists] = useState<string[]>([]);
    const [isLandr, setIsLandr] = useState(false);
    const [providerName, setProviderName] = useState('');
    const [canUploadCsv, setCanUploadCsv] = useState(false);

    // HOOKS
    const { notify } = useNotification();
    const history = useHistory();

    useEffect(() => {
        const handleToken = async (_authToken: string): Promise<void> => {
            history.push({
                ...history.location,
                hash: undefined,
            });

            setAuthToken(_authToken);

            try {
                const response = await validateToken(_authToken);

                setLabels(response.labels);
                setArtists(response.artists);
                setProviderName(response.providerName);
                setCanUploadCsv(response.canUploadMetadata);

                if (
                    response.providerName === 'LANDR' ||
                    response.providerName === 'LANDR PAID'
                ) {
                    setIsLandr(true);
                }
            } catch (error) {
                localStorage.removeItem('token');

                notify({
                    variant: 'danger',
                    content: (
                        <Trans>
                            There was a problem validating your credentials.
                            Please try logging in again.
                        </Trans>
                    ),
                });

                log.error('', EventIds.TokenValidationError, error as Error);
                authenticationService.signout();

                return;
            }
            localStorage.setItem('token', _authToken);
            setIsAuthenticated(true);
        };

        const fetchUser = async (): Promise<void> => {
            const isAuthenticated = await authenticationService.isAuthenticated();

            if (isAuthenticated) {
                const user = await authenticationService.getAdminProfile();
                handleToken(user.accessToken);
            } else {
                const urlParams = new URLSearchParams(window.location.search);
                const urlParamsToString = urlParams.toString();

                await authenticationService.login(
                    `targetPath=${window.location.pathname}&${urlParamsToString}`,
                );
            }
        };

        fetchUser();
        // only run on mount.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const logout = useCallback(() => {
        localStorage.removeItem('authToken');
        setIsAuthenticated(false);
        authenticationService.signout();
    }, [setIsAuthenticated]);

    return {
        isAuthenticated,
        authToken,
        logout,
        labels,
        artists,
        isLandr,
        providerName,
        canUploadCsv,
    };
};
